import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, Sun, Moon } from 'lucide-react';
import Logo from '@components/ui/Logo';
import { useTheme } from '@context/ThemeContext';

const Section = ({ title, children }) => (
  <section className="mb-8">
    <h2 className="text-lg font-bold text-surface-900 dark:text-white mb-3">{title}</h2>
    <div className="space-y-3 text-sm leading-relaxed text-surface-600 dark:text-surface-400">
      {children}
    </div>
  </section>
);

const TermsOfServicePage = () => {
  const { dark, toggleTheme } = useTheme();

  return (
    <div className="min-h-screen bg-surface-50 dark:bg-surface-950">
      <header className="sticky top-0 z-40 glass-header">
        <div className="w-full max-w-4xl mx-auto flex items-center justify-between px-4 sm:px-6 h-16">
          <Link to="/" className="flex items-center gap-2 text-surface-500 hover:text-surface-800 dark:hover:text-white transition-colors">
            <ArrowLeft size={18} />
            <Logo size="sm" />
          </Link>
          <button onClick={toggleTheme} className="theme-toggle" aria-label="Toggle dark mode">
            {dark ? <Sun size={18} strokeWidth={2} /> : <Moon size={18} strokeWidth={2} />}
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 pb-20">
        <div className="flex items-center gap-3 mb-2">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-violet-50 dark:bg-violet-500/10">
            <FileText size={20} className="text-violet-500" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-surface-900 dark:text-white">
            Terms of Service
          </h1>
        </div>
        <p className="text-xs text-surface-400 dark:text-surface-500 mb-10">
          Last updated: March 12, 2026
        </p>

        <div className="rounded-2xl border border-surface-200/60 bg-white p-6 sm:p-8 shadow-sm dark:border-surface-800/60 dark:bg-surface-900">
          <Section title="1. Acceptance of Terms">
            <p>
              By accessing or using Yaarvi (the &ldquo;Service&rdquo;), you agree to be bound by
              these Terms of Service (&ldquo;Terms&rdquo;). If you do not agree to these Terms, you
              may not use the Service. We reserve the right to update these Terms at any time, and
              your continued use of the Service constitutes acceptance of any changes.
            </p>
          </Section>

          <Section title="2. Description of Service">
            <p>
              Yaarvi is a web platform that helps users discover nearby people who want to
              participate in the same vibes &mdash; such as sports, games, study sessions,
              fitness, and more. The Service facilitates online connections between users based on
              shared interests and geographic proximity.
            </p>
          </Section>

          <Section title="3. Account Registration">
            <p>
              To use certain features of Yaarvi, you must create an account by signing in with
              Google. You agree to provide accurate and complete information during registration.
              You are responsible for maintaining the security of your account credentials and for
              all vibes that occur under your account.
            </p>
          </Section>

          <Section title="4. Acceptable Use">
            <p>You agree to use the Service only for lawful purposes. When using Yaarvi, you must NOT:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>Harass, bully, threaten, or intimidate other users.</li>
              <li>Post false, misleading, or inappropriate content.</li>
              <li>Impersonate another person or misrepresent your identity.</li>
              <li>Use the platform for any illegal, fraudulent, or unauthorized purpose.</li>
              <li>Attempt to gain unauthorized access to the Service, other accounts, or our systems.</li>
              <li>Use bots, scrapers, or automated tools to access or collect data from the Service.</li>
              <li>Spam, solicit, or send unsolicited messages to other users.</li>
              <li>Upload content that infringes on intellectual property rights of others.</li>
            </ul>
          </Section>

          <Section title="5. User-Generated Content">
            <p>
              You are solely responsible for any content you create on Yaarvi, including vibe
              listings, profile information, and messages. By posting content, you grant Yaarvi a
              non-exclusive, worldwide, royalty-free license to use, display, and distribute that
              content in connection with operating the Service. We reserve the right to remove any
              content that violates these Terms.
            </p>
          </Section>

          <Section title="6. Offline Interactions">
            <p>
              <strong className="text-surface-800 dark:text-surface-200">
                Yaarvi only facilitates online connections between users. We are not responsible
                for any offline interactions, meetups, or vibes that occur as a result of using
                the Service.
              </strong>{' '}
              You participate in any real-world vibes at your own risk. We strongly encourage
              users to meet in public places, inform someone they trust about their plans, and
              exercise caution when meeting people in person.
            </p>
          </Section>

          <Section title="7. Account Suspension &amp; Termination">
            <p>
              We reserve the right to suspend or permanently terminate your account if you violate
              these Terms, engage in abusive behavior, or use the Service in a manner that could
              harm other users or Yaarvi. We may take action at our sole discretion, with or
              without prior notice. You may also delete your account at any time by visiting the{' '}
              <Link to="/delete-account" className="text-brand-500 hover:text-brand-600 font-medium underline underline-offset-2">
                Data Deletion page
              </Link>.
            </p>
          </Section>

          <Section title="8. Intellectual Property">
            <p>
              All content, trademarks, logos, and intellectual property on Yaarvi are owned by or
              licensed to Yaarvi. You may not reproduce, distribute, or create derivative works
              from any part of the Service without our express written permission.
            </p>
          </Section>

          <Section title="9. Disclaimer of Warranties">
            <p>
              The Service is provided on an &ldquo;as is&rdquo; and &ldquo;as available&rdquo; basis
              without warranties of any kind, either express or implied. We do not guarantee that the
              Service will be uninterrupted, error-free, or free of harmful components. Your use of
              the Service is at your sole risk.
            </p>
          </Section>

          <Section title="10. Limitation of Liability">
            <p>
              To the fullest extent permitted by law, Yaarvi and its team shall not be liable for
              any indirect, incidental, special, consequential, or punitive damages arising out of or
              related to your use of the Service, including but not limited to damages for loss of
              profits, data, or other intangible losses &mdash; even if we have been advised of the
              possibility of such damages.
            </p>
          </Section>

          <Section title="11. Indemnification">
            <p>
              You agree to indemnify and hold harmless Yaarvi, its team, and affiliates from any
              claims, damages, losses, or expenses (including legal fees) arising out of your use of
              the Service, your violation of these Terms, or your violation of any rights of a third
              party.
            </p>
          </Section>

          <Section title="12. Governing Law">
            <p>
              These Terms shall be governed by and construed in accordance with the laws of India.
              Any disputes arising under these Terms shall be subject to the exclusive jurisdiction
              of the courts located in India.
            </p>
          </Section>

          <Section title="13. Contact Us">
            <p>
              If you have any questions about these Terms of Service, please contact us at:
            </p>
            <p className="mt-2">
              <a href="mailto:support@yaarvi.com" className="text-brand-500 hover:text-brand-600 font-semibold underline underline-offset-2">
                support@yaarvi.com
              </a>
            </p>
          </Section>
        </div>

        <div className="mt-8 flex flex-wrap gap-4 text-xs text-surface-400 dark:text-surface-500">
          <Link to="/privacy" className="hover:text-brand-500 transition-colors underline underline-offset-2">Privacy Policy</Link>
          <Link to="/delete-account" className="hover:text-brand-500 transition-colors underline underline-offset-2">Delete Account</Link>
        </div>
      </main>
    </div>
  );
};

export default TermsOfServicePage;
